import React, { useState } from "react";
import { Loader2, Calculator } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const inputCls = "w-full mt-1 px-3 py-2 rounded-lg nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white font-semibold";
const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);
const money4 = (n) => (n == null ? "—" : `$${Number(n).toFixed(4)}`);

function Row({ label, value, strong }) {
  return (
    <div className={`flex justify-between py-1.5 border-b border-nbblack/5 ${strong ? "font-head font-extrabold text-lg" : "text-sm"}`}>
      <span className={strong ? "" : "text-nbblack/60"}>{label}</span>
      <span data-testid={`sim-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{value}</span>
    </div>
  );
}

export default function AdminSimulator({ cfg }) {
  const [form, setForm] = useState({
    material_key: "white_vinyl", cut_type: "die_cut", width_in: 3, height_in: 3,
    shape: "rectangle", quantity: 100,
  });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const run = async () => {
    setBusy(true);
    try {
      const payload = {
        width_in: Number(form.width_in),
        height_in: Number(form.height_in),
        material_key: form.material_key,
        cut_type: form.cut_type,
        quantity: Number(form.quantity),
        shape: form.shape,
      };
      const { data } = await api.post("/admin/price/simulate", payload);
      setRes(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simulation failed (this configuration may require a manual quote).");
      setRes(null);
    } finally {
      setBusy(false);
    }
  };

  const ma = res?.margin_analysis;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Inputs */}
      <div className="bg-white nb-border nb-shadow rounded-2xl p-6 space-y-4">
        <h3 className="font-head font-extrabold text-xl">Pricing simulator</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-nbblack/60">Material</label>
            <select value={form.material_key} onChange={(e) => set("material_key", e.target.value)} className={inputCls} data-testid="sim-material">
              {(cfg?.materials || []).map((m) => <option key={m.key} value={m.key}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-nbblack/60">Cut type</label>
            <select value={form.cut_type} onChange={(e) => set("cut_type", e.target.value)} className={inputCls} data-testid="sim-cut">
              {(cfg?.cut_types || []).map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-nbblack/60">Width (in)</label>
            <input type="number" step="0.5" value={form.width_in} onChange={(e) => set("width_in", e.target.value)} className={inputCls} data-testid="sim-width" />
          </div>
          <div>
            <label className="text-xs font-bold text-nbblack/60">Height (in)</label>
            <input type="number" step="0.5" value={form.height_in} onChange={(e) => set("height_in", e.target.value)} className={inputCls} data-testid="sim-height" />
          </div>
          <div>
            <label className="text-xs font-bold text-nbblack/60">Shape</label>
            <select value={form.shape} onChange={(e) => set("shape", e.target.value)} className={inputCls} data-testid="sim-shape">
              <option value="contour_cut">Contour cut</option>
              <option value="rectangle">Rectangle / square</option>
              <option value="circle">Circle</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-nbblack/60">Quantity (sheets for sheet cut)</label>
            <input type="number" step="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className={inputCls} data-testid="sim-quantity" />
          </div>
        </div>
        <button onClick={run} disabled={busy} data-testid="sim-run" className="inline-flex items-center gap-2 bg-nbpink text-white font-head font-bold px-6 py-3 rounded-full nb-border nb-shadow nb-press disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Calculator size={18} />} Simulate
        </button>
      </div>

      {/* Results */}
      <div className="bg-white nb-border nb-shadow rounded-2xl p-6" data-testid="sim-results">
        {!res ? (
          <p className="text-nbblack/50 font-semibold py-10 text-center">Run a simulation to see the full breakdown & margin analysis.</p>
        ) : (
          <div>
            <h3 className="font-head font-extrabold text-xl mb-3">Breakdown</h3>
            <Row label="Product type" value={res.product_type} />
            <Row label="Nesting efficiency" value={res.nesting_efficiency} />
            <Row label="Finished area (sq in)" value={Number(res.finished_area_sq_in).toFixed(2)} />
            <Row label="Production area (sq ft)" value={Number(res.production_area_sq_ft).toFixed(4)} />
            <Row label="Progressive area charge" value={money4(res.progressive_area_charge)} />
            <Row label="Material surcharge" value={money4(res.material_surcharge)} />
            <Row label="Setup fee" value={money(res.setup_fee)} />
            <Row label={`Cut path (${Number(res.cut_path_inches).toFixed(0)} in)`} value={money4(res.cut_path_charge)} />
            <Row label="Handling" value={money4(res.handling_charge)} />
            <Row label={`Packaging (${res.packaging_tier})`} value={money(res.packaging_charge)} />
            <Row label="Shipping reserve" value={money(res.shipping_reserve)} />
            <Row label="Calculated retail" value={money4(res.calculated_retail_price)} />
            <Row label="Minimum job price" value={money(res.minimum_job_price)} />
            <Row label="Minimum profitable price" value={money4(res.minimum_profitable_price)} />

            <h3 className="font-head font-extrabold text-xl mt-5 mb-3">Result</h3>
            <Row label="Final total" value={money(res.total)} strong />
            <Row label={res.is_sheet ? "Price per sheet" : "Unit price"} value={money(res.is_sheet ? res.price_per_sheet : res.unit_price)} />
            {res.is_sheet && <Row label="Effective price / sticker" value={money(res.effective_price_per_sticker)} />}
            {res.is_sheet && <Row label="Stickers per sheet" value={res.per_sheet} />}

            {ma && (
              <>
                <h3 className="font-head font-extrabold text-xl mt-5 mb-3">Margin analysis</h3>
                <Row label="Internal direct cost" value={money4(ma.internal_direct_cost)} />
                <Row label="Payment processing" value={money4(ma.payment_processing)} />
                <Row label="Gross profit" value={money4(ma.gross_profit)} />
                <Row label="Realized gross margin" value={`${ma.gross_margin_pct}%`} strong />
                <div className="mt-2 flex gap-2 flex-wrap">
                  {ma.margin_floor_applied && <span className="px-3 py-1 rounded-full text-xs font-bold border-2 bg-orange-100 text-orange-700 border-orange-300" data-testid="sim-floor-badge">Margin floor applied</span>}
                  {ma.minimum_job_applied && <span className="px-3 py-1 rounded-full text-xs font-bold border-2 bg-blue-100 text-blue-700 border-blue-300">Minimum job applied</span>}
                  {!ma.margin_floor_applied && !ma.minimum_job_applied && <span className="px-3 py-1 rounded-full text-xs font-bold border-2 bg-green-100 text-green-700 border-green-300">Retail curve applied</span>}
                </div>
              </>
            )}

            {res.upsell && (
              <div className="mt-5 bg-nbyellow nb-border rounded-xl p-3 text-sm" data-testid="sim-upsell">
                <b>Recommended upsell:</b> upgrade to {res.upsell.quantity} (+{res.upsell.additional_quantity}) for {money(res.upsell.additional_cost)} more — save {res.upsell.unit_savings_pct}% per unit.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
